<template>
  <v-list-item three-line @click="doNothing()">
    <v-list-item-avatar v-if="c.user" @click.stop="goToMember(c.user._id)">
      <img :src="userFile(c.user.userImageSmall)" />
    </v-list-item-avatar>
    <v-list-item-avatar v-else-if="c.visitorData" @click.stop="goToVisitor(c.visitorData._id)">
      <img :src="assets() + '/default-profile-avatar.png'" />
    </v-list-item-avatar>
    <v-list-item-content>
      <v-list-item-title>
        <span v-if="c.name && !hideClass" class="gray--text">{{ c.name }}</span>
        <span v-if="(c.user || c.visitorData) && !hideClass" class="ml-1 mr-1 gray--text">•</span>
        <span v-if="c.user">
          <v-tooltip top v-if="c.isAssistantProfessor">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-account-supervisor-outline</v-icon>
            </template>
            <span>{{ $t("message.assistant_professor") }}</span>
          </v-tooltip>
          <v-tooltip top v-else-if="c.isProfessor">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-account-star-outline</v-icon>
            </template>
            <span>{{ $t("message.professor") }}</span>
          </v-tooltip>
          {{ c.user.displayName }}
          <v-tooltip v-if="c.isLate" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" size="20" color="error">mdi-clock-alert-outline</v-icon>
            </template>
            <span>{{ $t("message.minutes_late", [c.isLate]) }}</span>
          </v-tooltip>
        </span>
        <span v-else-if="c.visitorData">{{ c.visitorData.name }}
          <v-tooltip v-if="c.isLate" top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" size="20" color="error">mdi-clock-alert-outline</v-icon>
            </template>
            <span>{{ $t("message.minutes_late", [c.isLate]) }}</span>
          </v-tooltip>
        </span>
        <Belt v-if="!c.curriculumInstance && c.user && !c.user.notBjjUser" class="mr-1" :show-text="false" :size="13"
          :belt="c.user.belt"></Belt>
        <Belt v-if="c.curriculumInstance && !c.curriculumInstance.useSyllabusRanks" class="ml-1" :show-text="false"
          :size="13" :belt="c.user.belt"></Belt>
      </v-list-item-title>
      <v-list-item-subtitle>
        {{ $d(new Date(c.date), 'short', $i18n.locale) }}
      </v-list-item-subtitle>
      <v-list-item-subtitle
        v-if="c.curriculumInstance && c.curriculumInstance.useSyllabusRanks && c.curriculumInstance.useBelts">
        <Belt :show-text="true" :size="13" :belt="c.curriculumInstance.level"></Belt>
        <span class="ml-1">({{ c.curriculumInstance.progress }}%)</span>
      </v-list-item-subtitle>
      <v-list-item-subtitle
        v-if="c.curriculumInstance && c.curriculumInstance.useSyllabusRanks && !c.curriculumInstance.useBelts">
        <span><b>{{ c.curriculumInstance.level }}</b></span>
        <span class="ml-1">({{ c.curriculumInstance.progress }}%)</span>
      </v-list-item-subtitle>
      <v-list-item-subtitle v-if="c.visitorData" class="primary--text">
        {{ $t('message.visitor') }}
      </v-list-item-subtitle>
      <v-list-item-subtitle v-else-if="c.membership && c.membership.transactionId">
        <span v-if="c.membership.subscriptionEndDate">
          <v-icon @click.stop="goToTransaction(c.membership.transactionId)"
            :color="getTransactionStatusColor(c.membership.status)">mdi-ticket-confirmation-outline</v-icon>
          {{ $t('message.expires') }}: {{ $d(new Date(c.membership.subscriptionEndDate),
            'month_day_year', $i18n.locale)
          }}
        </span>
        <span v-else-if="c.membership.subscriptionCheckins > 0">
          <v-icon @click.stop="goToTransaction(c.membership.transactionId)"
            :color="getTransactionStatusColor(c.membership.status)">mdi-ticket-confirmation-outline</v-icon>
          {{ $t('message.expires') }}: {{ c.membership.subscriptionCheckins }}
          {{ $t('message.checkins') }}
        </span>
        <span v-else>
          <v-icon @click.stop="goToTransaction(c.membership.transactionId)"
            :color="getTransactionStatusColor(c.membership.status)">mdi-sync</v-icon>
          {{ c.membership.product ? c.membership.product : $t('message.subscription') }}
        </span>
      </v-list-item-subtitle>
      <v-list-item-subtitle v-else-if="c.freeClass" class="gray--text">
        <v-icon>mdi-currency-usd-off</v-icon>
        {{ $t('message.free') }}
      </v-list-item-subtitle>
      <v-list-item-subtitle v-else class="gray--text">
        <v-icon>mdi-ticket-confirmation-outline</v-icon>
        {{ $t('message.alert_no_membership') }}
      </v-list-item-subtitle>
    </v-list-item-content>
    <v-list-item-action v-if="c.daysUntilBirthdate == 0 || (c.daysUntilBirthdate < 0 && c.daysUntilBirthdate >= -3)">
      <v-badge v-if="c.daysUntilBirthdate < 0 && c.daysUntilBirthdate >= -3" color="secondary"
        :content="c.daysUntilBirthdate" overlap class="bday-floater">
        <v-btn x-small icon color="accent">
          <v-icon>mdi-cake-variant-outline</v-icon>
        </v-btn>
      </v-badge>
      <v-btn v-else-if="c.daysUntilBirthdate == 0" small icon overlap class="bday-floater" color="accent">
        <v-icon>mdi-cake-variant-outline</v-icon>
      </v-btn>
    </v-list-item-action>
    <v-list-item-action v-if="!isSmallerDisplay() && c.instanceId">
      <v-btn icon @click.stop="levelUp(c)">
        <v-icon>mdi-chevron-double-up</v-icon>
      </v-btn>
    </v-list-item-action>
    <v-list-item-action v-if="!isSmallerDisplay() && c.alerts && c.alertCounts">
      <v-btn text elevation="0" color="error" @click.stop="displayAlerts(c)">
        <v-badge class="mr-3" :color="c.alertCounts.docs ? 'error' : ''" :content="c.alertCounts.docs" overlap>
          <v-icon :color="c.alertCounts.docs ? 'error' : '#BDBDBD'">mdi-file-sign</v-icon>
        </v-badge>
        <v-badge class="mr-3" :color="c.alertCounts.memberships ? 'error' : ''" :content="c.alertCounts.memberships"
          overlap>
          <v-icon :color="c.alertCounts.memberships ? 'error' : '#BDBDBD'">mdi-wallet-membership</v-icon>
        </v-badge>
        <v-badge :color="c.alertCounts.other ? 'error' : ''" :content="c.alertCounts.other" overlap>
          <v-icon :color="c.alertCounts.other ? 'error' : '#BDBDBD'">mdi-alert-outline</v-icon>
        </v-badge>
      </v-btn>
    </v-list-item-action>
    <v-list-item-action v-if="isSmallerDisplay() && c.alerts && c.alertCounts">
      <v-badge elevation="0" color="secondary" :content="c.alerts" overlap class="alerts-floater">
        <v-btn x-small elevation="0" fab color="error" @click.stop="displayAlerts(c)">
          <v-icon>mdi-alert-outline</v-icon>
        </v-btn>
      </v-badge>
    </v-list-item-action>
    <v-list-item-action v-else-if="isSmallerDisplay() && c.instanceId">
      <v-btn icon @click.stop="levelUp(c)">
        <v-icon>mdi-chevron-double-up</v-icon>
      </v-btn>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import Belt from "@/components/displays/BeltDisplay.vue";

export default {
  props: ["c", "hideClass", "displayAlerts", "levelUp"],
  data() {
    return {
    };
  },
  components: {
    Belt,
  },
};
</script>

<style scoped></style>
